/** @file Lays out one graph frame: curve polylines, key points and the handles of selected keys. */
import { timelineScale } from '@pose2d/editor-geometry/timelineScale';
import type { TimelineScale } from '@pose2d/editor-geometry/types';
import { CURVE_GRAPH } from './curveGraph.data';
import { CURVE_POINT } from './curvePoint.data';
import { curveSegment } from './curveSegment';
import { curveValueScale } from './curveValueScale';
import type { CurvePoints, CurvePreview, CurveScene, CurveTrack, CurveTrackMap, CurveValueScale } from './curveTypes';

/** Everything one frame needs; `selected[t]` holds the frames of track t's selected keys. */
export interface CurveSceneInput {
  tracks: readonly CurveTrack[];
  maps: readonly CurveTrackMap[];
  time: TimelineScale;
  value: CurveValueScale;
  width: number;
  selected: readonly ReadonlySet<number>[];
  preview: CurvePreview | null;
}

/** Canvas mapping of one track plus the shared output. */
type Ctx = { x: (frame: number) => number; y: (value: number) => number; width: number; points: CurvePoints; t: number };
type Point = Omit<Record<keyof CurvePoints, number>, 'track'>;
const FIELDS = ['x', 'y', 'kind', 'index', 'which', 'selected', 'ax', 'ay'] as const;
const NO_ANCHOR = { ax: Number.NaN, ay: Number.NaN };

function push(c: Ctx, p: Point): void {
  for (const field of FIELDS) c.points[field].push(p[field]);
  c.points.track.push(c.t);
}

/** Canvas point of key i (progress tracks: the start of its segment, at 0). */
function keyPoint(c: Ctx, track: CurveTrack, i: number): [number, number] {
  return [c.x(track.frames[i] ?? 0), c.y(curveSegment.ends(track, i)[0])];
}

/** Steps for a segment from x0 to x1: one per 2 px while on screen (capped), a single step off screen. */
function sampleCount(c: Ctx, x0: number, x1: number): number {
  if (x1 < 0 || x0 > c.width) return 1;
  return Math.min(CURVE_GRAPH.maxSamples, Math.max(1, Math.ceil((x1 - x0) / CURVE_GRAPH.samplePx)));
}

/** Samples segment i into `line`, adds the samples as points, draws a stepped jump, breaks progress lines. */
function addSegment(c: Ctx, track: CurveTrack, i: number, line: number[]): void {
  const f0 = track.frames[i] ?? 0;
  const f1 = track.frames[i + 1] ?? f0;
  const n = sampleCount(c, c.x(f0), c.x(f1));
  for (let k = 0; k <= n; k++) {
    const x = c.x(f0 + ((f1 - f0) * k) / n);
    const y = c.y(curveSegment.valueAt(track, i, k / n));
    line.push(x, y);
    if (k > 0) push(c, { x, y, kind: CURVE_POINT.sample, index: i, which: 0, selected: 0, ...NO_ANCHOR });
  }
  const [v0, v1] = curveSegment.ends(track, i);
  if (curveSegment.code(track, i) === 1 && v1 !== v0) line.push(c.x(f1), c.y(v1));
  if (track.mode === 'progress') line.push(Number.NaN, Number.NaN);
}

/** Polyline of a track; value tracks hold their first value from the left edge and their last to the right. */
function trackLine(c: Ctx, track: CurveTrack): number[] {
  const line: number[] = [];
  const last = track.frames.length - 1;
  const [xFirst, yFirst] = keyPoint(c, track, 0);
  const [xLast, yLast] = keyPoint(c, track, last);
  if (track.mode === 'value') line.push(Math.min(0, xFirst), yFirst, xFirst, yFirst);
  for (let i = 0; i < last; i++) addSegment(c, track, i, line);
  if (track.mode === 'value') line.push(xLast, yLast, Math.max(c.width, xLast), yLast);
  return line;
}

/** Both handles of segment i: control point 1 hangs from key i, control point 2 from the segment's end. */
function addHandles(c: Ctx, track: CurveTrack, i: number, anchor: [number, number]): void {
  const [x1, cy1, x2, cy2] = curveSegment.controls(track, i);
  const f0 = track.frames[i] ?? 0;
  const f1 = track.frames[i + 1] ?? f0;
  const handle = { kind: CURVE_POINT.handle, index: i, selected: 1 };
  push(c, { ...handle, x: c.x(f0 + x1 * (f1 - f0)), y: c.y(cy1), which: 1, ax: anchor[0], ay: anchor[1] });
  push(c, { ...handle, x: c.x(f0 + x2 * (f1 - f0)), y: c.y(cy2), which: 2, ax: c.x(f1), ay: c.y(curveSegment.ends(track, i)[1]) });
}

/** Key points, plus the handles of every selected key whose outgoing segment exists and is not stepped. */
function addKeys(c: Ctx, track: CurveTrack, selected: ReadonlySet<number>): void {
  track.frames.forEach((frame, i) => {
    const [x, y] = keyPoint(c, track, i);
    const on = selected.has(frame) ? 1 : 0;
    push(c, { x, y, kind: CURVE_POINT.key, index: i, which: 0, selected: on, ...NO_ANCHOR });
    if (on === 1 && i < track.frames.length - 1 && curveSegment.code(track, i) !== 1) addHandles(c, track, i, [x, y]);
  });
}

/** The tracks with the previewed segment's curve swapped in (the document is never touched). */
function withPreview(tracks: readonly CurveTrack[], preview: CurvePreview | null): CurveTrack[] {
  if (preview === null) return [...tracks];
  const swap = (track: CurveTrack): CurveTrack => ({ ...track, curves: track.curves.map((curve, i) => (i === preview.segment ? preview.curve : curve)) });
  return tracks.map((track, t) => (t === preview.track ? swap(track) : track));
}

/** Builds the frame: lines[t] for track t and every pickable point, in canvas pixels. */
export function buildCurveScene(input: CurveSceneInput): CurveScene {
  const points: CurvePoints = { x: [], y: [], kind: [], track: [], index: [], which: [], selected: [], ax: [], ay: [] };
  const tracks = withPreview(input.tracks, input.preview);
  const lines = tracks.map((track, t) => {
    const map = input.maps[t] ?? { offset: 0, gain: 1 };
    const c: Ctx = {
      x: (frame) => timelineScale.frameToX(input.time, frame),
      y: (value) => curveValueScale.toY(input.value, (value - map.offset) * map.gain),
      width: input.width,
      points,
      t,
    };
    const line = trackLine(c, track);
    addKeys(c, track, input.selected[t] ?? new Set());
    return line;
  });
  return { tracks, lines, points };
}
